/* global GM_STATE */
import { getChildStockStatus } from '@/components/helpers/bundle-items'
import { openModal } from '@/components/helpers/dom'
import {
  STORAGE_1P_KEY,
  STORAGE_2P_KEY,
  formatPreorderDate,
  getBothWarehousesStatus,
  getStockStatus,
  getMetafieldKey,
  parseStockJson,
  pickEarliestDate,
  pickLatestDate,
  resolveActiveWarehouse
} from 'helpers/stock-status'
import { emitterProps, globalEmitter } from 'helpers/emitter-event'
import { addCartItem, getCart, getMaxQuantityByVariant } from 'libs/cart'
import { useEffect, useMemo, useState } from 'preact/hooks'
import ButtonATC from 'snippets/button-atc/button-atc'
import ButtonATCDisabled from 'snippets/button-atc/button-atc-disabled'
import Icon from 'snippets/icon/icon'
import ProductStatus from 'snippets/product-status/product-status'
import QuantityInput from 'snippets/quantity-input/quantity-input'

function ProductATC({
  currentVariant,
  initProduct,
  buttonText,
  buttonClass = '',
  buttonWrapperClass = 'mt-4 md:mt-6',
  showStatus = false,
  isAnchorSection = false,
  showQty = false
}) {
  const [isAdding, setIsAdding] = useState(false)
  const [quantity, setQuantity] = useState(1)
  const [cartItems, setCartItems] = useState(GM_STATE.cart.initCart.items)
  const [maxQuantity, setMaxQuantity] = useState(null)
  const [isLoading, setIsLoading] = useState(true)
  const [reRenderKey, setReRenderKey] = useState(1)
  const [bundleAvailability, setBundleAvailability] = useState('available')
  const [bundleStatusLoading, setBundleStatusLoading] = useState(true)
  const [bundlePreOrderDates, setBundlePreOrderDates] = useState([])
  const [bundleStockQty, setBundleStockQty] = useState(null)
  const [warehouse1P, setWarehouse1P] = useState(() => localStorage.getItem(STORAGE_1P_KEY) || null)
  const [warehouse2P, setWarehouse2P] = useState(() => localStorage.getItem(STORAGE_2P_KEY) || null)
  const notifyMeWhenAvailable = GM_STATE.apps.backInStock.notifyMeWhenAvailable
  const klaviyoNotifyPopupId = GM_STATE.apps.backInStock.klaviyoNotifyPopupId

  // Resolve warehouse-based stock status
  const hasPostcodeInput = warehouse1P !== null || warehouse2P !== null
  const activeWarehouse = hasPostcodeInput
    ? resolveActiveWarehouse(warehouse1P, warehouse2P, currentVariant)
    : null
  const warehouseStockData = activeWarehouse
    ? parseStockJson(currentVariant.metafields?.[getMetafieldKey(activeWarehouse)])
    : null

  // With a postcode set: use the resolved warehouse's stockstatusmessage. With no postcode: the
  // best of Brisbane/Melbourne wins, falling back to Shopify's own availability if neither has
  // usable data (see getBothWarehousesStatus).
  const noPostcodeStatus = !hasPostcodeInput
    ? getBothWarehousesStatus(
      parseStockJson(currentVariant.metafields?.brisbane_stock_json),
      parseStockJson(currentVariant.metafields?.melbourne_stock_json),
      currentVariant.available
    )
    : null

  const { isPreorder, isOutOfStock } = activeWarehouse
    ? getStockStatus(warehouseStockData, currentVariant, 0)
    : hasPostcodeInput
      ? { isPreorder: false, isOutOfStock: false }
      : noPostcodeStatus
  const preOrderDate = activeWarehouse && warehouseStockData?.earliestpodate
    ? warehouseStockData.earliestpodate
    : ''
  // Two warehouses stocking one variant are alternatives — we'd source from whichever lands first.
  const noPostcodeEarliestDate = pickEarliestDate(noPostcodeStatus?.earliestPODates)

  const bundleComponentSKUs = initProduct?.metafields?.bundle_child_skus ? initProduct?.metafields?.bundle_child_skus.split(', ') : []
  const isBundle = bundleComponentSKUs.length > 0

  // Bundle children are the opposite: every one must arrive, so the latest date is the real wait.
  const resolvedPODate = useMemo(
    () => pickLatestDate([...bundlePreOrderDates, noPostcodeEarliestDate]),
    [bundlePreOrderDates, noPostcodeEarliestDate]
  )

  // ProductStatus does its own parsing/formatting, so it needs the unformatted date.
  const earliestPODateRaw = resolvedPODate || preOrderDate || ''

  const earliestPODate = useMemo(() => {
    if (!resolvedPODate) {
      return preOrderDate || ''
    }

    return new Date(resolvedPODate).toLocaleDateString('en-AU', {
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    })
  }, [resolvedPODate, preOrderDate])

  const bundleComposition = bundleComponentSKUs?.map((childItem) => {
    const bundleObj = {}
    bundleObj.sku = childItem.split('#')[0]
    bundleObj.qty = parseInt(childItem.split('#')[1])
    return bundleObj
  })

  const bundleCompositionMarker = bundleComposition.map((x) => x.sku + x.qty).join()

  useEffect(() => {
    const onPostcodeUpdated = (e) => {
      setWarehouse1P(e.detail.warehouse1P ?? null)
      setWarehouse2P(e.detail.warehouse2P ?? null)
    }
    window.addEventListener('postcode:updated', onPostcodeUpdated)
    return () => window.removeEventListener('postcode:updated', onPostcodeUpdated)
  }, [])

  useEffect(() => {
    if (quantity === undefined || quantity === null) {
      setQuantity(1)
    }
  }, [])

  useEffect(() => {
    if (!isBundle) {
      setBundleStatusLoading(false)
      return
    }

    let cancelled = false
    setBundleStatusLoading(true)

    getChildStockStatus(bundleComposition, { warehouse1P, warehouse2P })
      .then((body) => {
        if (cancelled) return
        setBundleAvailability(body?.childrenBundleStatus)
        setBundlePreOrderDates(body?.poDates || [])
        setBundleStockQty(body?.bundleStockQty ?? null)
      })
      .catch((err) => {
        console.error('Some error getting the bundle item status', err)
        if (!cancelled) {
          setBundleAvailability('Out of stock')
        }
      })
      .finally(() => {
        if (!cancelled) setBundleStatusLoading(false)
      })

    return () => {
      cancelled = true
    }
  }, [bundleCompositionMarker, warehouse1P, warehouse2P])

  // Bundle is available
  const isRegularAvailableProduct = currentVariant.available && maxQuantity > 0

  // Bundle parents carry no warehouse blob of their own and Shopify derives their availability from
  // child inventory, so for bundles the children are the only signal we trust.
  const showOutOfStock = isBundle
    ? bundleAvailability === 'Out of stock'
    : Boolean(isOutOfStock)
  const showPreorder = isBundle ? bundleAvailability === 'Pre-order' : isPreorder

  // Bundle status is client-requested UI driven by the child warehouse blobs, so it renders
  // independently of the `showStatus` prop — and never without a postcode or in the sticky bar.
  const showBundleStatus = isBundle && hasPostcodeInput && !isAnchorSection

  const { productPackage } = window.productState

  const resetQuantity = (value = 1) => {
    setQuantity(value)
    setReRenderKey(reRenderKey + 1)
  }

  const addToCart = async () => {
    setIsAdding(true)
    let properties = {}
    if (productPackage) {
      properties = {
        _package: productPackage
      }
    }

    if (showPreorder) {
      properties = {
        ...properties,
        _preorderDate: earliestPODate
      }
    }

    try {
      const cartSingle = await addCartItem(currentVariant.id, properties, quantity)

      if (cartSingle) {
        globalEmitter.emit(emitterProps.cart.add, cartSingle)
      }

      const max = getMaxQuantityByVariant(cartSingle.items, currentVariant)

      if (max > 0 && max < quantity) {
        resetQuantity(max)
      }

      if (max <= 0) {
        resetQuantity(1)
      }

      syncMaxQuantity()
    } catch (err) {
      console.error('Error adding to cart:', err)
    } finally {
      setIsAdding(false)
    }
  }

  const handleAddToCart = () => addToCart()

  const getMaxQuantity = async (variant) => {
    const cartData = await getCart()
    setCartItems(cartData.items)
    return getMaxQuantityByVariant(cartData.items, variant)
  }

  const syncMaxQuantity = async () => {
    setIsLoading(true)
    const maxValue = await getMaxQuantity(currentVariant)
    setMaxQuantity(maxValue)
    setIsLoading(false)
  }

  const updateMaxQuantity = (items) => {
    setIsLoading(true)
    const maxValue = getMaxQuantityByVariant(items, currentVariant)
    setCartItems(items)
    setMaxQuantity(maxValue)
    setIsLoading(false)
  }

  const cartUpdateListener = (items) => updateMaxQuantity(items, currentVariant)

  const openNotifyModal = () => {
    window._klOnsite = window._klOnsite || []
    window._klOnsite.push(['openForm', klaviyoNotifyPopupId])
  }

  useEffect(() => {
    if (currentVariant) {
      updateMaxQuantity(cartItems, currentVariant)
      globalEmitter.on(emitterProps.cart.update, cartUpdateListener)
    }
    return () => {
      globalEmitter.off(emitterProps.cart.update, cartUpdateListener)
    }
  }, [currentVariant])

  if (isLoading || (isBundle && bundleStatusLoading)) {
    return (
      <div className={buttonWrapperClass}>
        <div className="mt-2 h-[56px] w-full rounded-full bg-grey-1000"></div>
        {showStatus && <ProductStatus maxQuantity={maxQuantity} isLoading={isLoading} initProduct={initProduct} />}
      </div>
    )
  }

  if (showOutOfStock) {
    return (
      <div className={buttonWrapperClass}>
        <div className="flex items-center gap-3">
          {!isAnchorSection && <ButtonATCDisabled text={translate.product.outStock} />}
        </div>
        {!isAnchorSection && typeof window !== 'undefined' && window.productState?.enableShopAssistantAi && (
          <div className="pt-6" data-shopassistant-container></div>
        )}
        {(showStatus || showBundleStatus) && (
          <ProductStatus
            maxQuantity={maxQuantity}
            isLoading={isLoading}
            initProduct={initProduct}
            isAvailable={false}
          />
        )}
      </div>
    )
  }

  if (showPreorder) {
    return (
      <div className={buttonWrapperClass}>
        <div className="flex items-center gap-3">
          <QuantityInput
            inputKey={`quickview-${currentVariant?.id || 'variant'}`}
            quantity={quantity ?? 1}
            min={1}
            max={maxQuantity || currentVariant?.inventory_quantity || 999}
            setQuantity={setQuantity}
            classWrap={`w-[109px] rounded-full h-12 ${showQty ? '' : 'hidden'}`}
            classButton="w-11 h-full p-0"
            iconClass="text-black"
          />
          <ButtonATC
            text="Pre-order"
            onHandle={handleAddToCart}
            loading={isAdding}
            buttonClass={buttonClass}
          />
        </div>
        <div className="mt-4">
          {!isAnchorSection && (
            <button
              className="button-outlined product-preorder-button flex w-full flex-row items-center justify-center gap-1.5 tracking-[0.84px] normal-case text-sm"
              onClick={() => openModal('modal-preorder')}
              title={window.translate.product.preOrderButton}
            >
              <Icon viewBox="0 0 20 20" name="icon-question" className="w-5 h-5 text-grey-500" />
              {window.translate.product.preOrderButton}
            </button>
          )}
        </div>
        {!isAnchorSection && typeof window !== 'undefined' && window.productState?.enableShopAssistantAi && (
          <div className="pt-6" data-shopassistant-container></div>
        )}
        {showBundleStatus && (
          <ProductStatus
            maxQuantity={maxQuantity}
            isLoading={isLoading}
            initProduct={initProduct}
            isPreorder={true}
            earliestPODate={earliestPODateRaw}
            isPreorderBundle={true}
          />
        )}
      </div>
    )
  }

  const isDisabled = isBundle ? showOutOfStock : !isRegularAvailableProduct

  if (isBundle || isRegularAvailableProduct) {
    return (
      <div className={buttonWrapperClass}>
        <div className="flex items-center gap-3">
          {isDisabled
            ? <ButtonATCDisabled text={'Out Of Stock'} />
            : <>
              <QuantityInput
                inputKey={`quickview-${currentVariant?.id || 'variant'}`}
                quantity={quantity ?? 1}
                min={1}
                max={maxQuantity || currentVariant?.inventory_quantity || 999}
                setQuantity={setQuantity}
                classWrap={`w-[122px] rounded-full h-12 ${showQty ? '' : 'hidden'}`}
                classButton="w-11 h-full p-0"
                iconClass="text-black"
              />
              <ButtonATC
                disabled={isDisabled}
                text={buttonText}
                onHandle={handleAddToCart}
                loading={isAdding}
                buttonClass={buttonClass}
              />
            </>}
        </div>
        {!isAnchorSection && typeof window !== 'undefined' && window.productState?.enableShopAssistantAi && (
          <div className="pt-6" data-shopassistant-container></div>
        )}
        {(showStatus || showBundleStatus) && (
          <ProductStatus
            // For a bundle the parent's maxQuantity is 9999 (oversell), so low stock has to come
            // from the scarcest child instead.
            maxQuantity={showBundleStatus ? (bundleStockQty ?? 9999) : maxQuantity}
            isLoading={isLoading}
            initProduct={initProduct}
            isAvailable={true}
          />
        )}
      </div>
    )
  }

  // Regular ATC
  return (
    <div className={buttonWrapperClass}>
      <div className="flex items-center gap-3">
        {!isAnchorSection && <ButtonATCDisabled text={translate.product.outStock} />}
      </div>
      {!isAnchorSection && typeof window !== 'undefined' && window.productState?.enableShopAssistantAi && (
        <div className="pt-6" data-shopassistant-container></div>
      )}
      {showStatus && (
        <ProductStatus
          maxQuantity={maxQuantity}
          isLoading={isLoading}
          initProduct={initProduct}
          isAvailable={false}
        />
      )}
    </div>
  )
}

export default ProductATC
